import { mutators, queries } from "@oblique/shared/zero"
import type { ProjectNodeInputs } from "@oblique/shared/runtime"
import { useQuery, useZero } from "@rocicorp/zero/react"
import { type Edge, type Node, useStoreApi } from "@xyflow/react"
import { useCallback, useRef } from "react"
import { useParams } from "react-router"
import { v7 as uuidv7 } from "uuid"
import type { Project_edgesRow, Project_nodesRow } from "@/types/zero"

const PASTE_OFFSET = 48
const RUNTIME_METADATA_KEYS = new Set([
	"status",
	"lastRequestId",
	"lastRunAt",
	"result",
	"error",
])

// FIXME: This is temporary until we have a proper record type.
function toRecord(value: unknown): Record<string, unknown> {
	if (!value) return Object.create(null)
	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value) as unknown
			if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
				return parsed as Record<string, unknown>
			}
		} catch {
			return Object.create(null)
		}
	}
	if (typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>
	}
	return Object.create(null)
}

function toPosition(value: unknown): { x: number; y: number } {
	const record = toRecord(value)
	const x = typeof record.x === "number" ? record.x : 0
	const y = typeof record.y === "number" ? record.y : 0
	return { x, y }
}

function toHandle(value: unknown): string | null {
	return typeof value === "string" && value.length > 0 ? value : null
}

function sanitizeMetadata(value: unknown): Record<string, unknown> {
	const metadata = { ...toRecord(value) }
	for (const key of RUNTIME_METADATA_KEYS) {
		delete metadata[key]
	}
	return metadata
}

function applySelection(
	storeApi: ReturnType<typeof useStoreApi<Node, Edge>>,
	nodeIds: string[],
	edgeIds: string[],
) {
	const state = storeApi.getState()
	const selectedNodeIds = new Set(nodeIds)
	const selectedEdgeIds = new Set(edgeIds)

	state.triggerNodeChanges(
		state.nodes.map((node) => ({
			id: node.id,
			type: "select",
			selected: selectedNodeIds.has(node.id),
		})),
	)
	state.triggerEdgeChanges(
		state.edges.map((edge) => ({
			id: edge.id,
			type: "select",
			selected: selectedEdgeIds.has(edge.id),
		})),
	)
}

export function useNodeCopyPaste() {
	const z = useZero()
	const { projectId } = useParams<{ projectId: string }>()
	const [project] = useQuery(queries.projects.byId(projectId ?? ""))
	const storeApi = useStoreApi<Node, Edge>()

	const clipboardRef = useRef<{
		nodes: readonly Project_nodesRow[]
		edges: readonly Project_edgesRow[]
	} | null>(null)
	const pasteCountRef = useRef(0)

	const copySelection = useCallback(() => {
		if (!project) return false

		const projectData = project as unknown as {
			project_nodes?: readonly Project_nodesRow[]
			project_edges?: readonly Project_edgesRow[]
		}
		const projectNodes = projectData.project_nodes ?? []
		const projectEdges = projectData.project_edges ?? []

		const selectedNodeIds = new Set(
			storeApi
				.getState()
				.nodes.filter((node) => node.selected)
				.map((node) => node.id),
		)
		if (selectedNodeIds.size === 0) return false

		const nodes = projectNodes.filter((node) => selectedNodeIds.has(node.id))
		if (nodes.length === 0) return false

		const edges = projectEdges.filter(
			(edge) =>
				selectedNodeIds.has(edge.source_node_id) &&
				selectedNodeIds.has(edge.target_node_id),
		)

		clipboardRef.current = { nodes, edges }
		pasteCountRef.current = 0
		return true
	}, [project, storeApi])

	const hasClipboardSelection = useCallback(
		() => Boolean(projectId && clipboardRef.current?.nodes.length),
		[projectId],
	)

	const pasteSelection = useCallback(async () => {
		if (!projectId || !clipboardRef.current) return false

		const clipboard = clipboardRef.current
		if (clipboard.nodes.length === 0) return false

		pasteCountRef.current += 1
		const offset = PASTE_OFFSET * pasteCountRef.current

		const nodeIdMap = new Map<string, string>()
		const pastedNodeIds: string[] = []
		for (const node of clipboard.nodes) {
			const id = uuidv7()
			nodeIdMap.set(node.id, id)
			pastedNodeIds.push(id)
		}

		for (const node of clipboard.nodes) {
			const newId = nodeIdMap.get(node.id)
			if (!newId) continue

			const position = toPosition(node.position)

			await z.mutate(
				mutators.nodes.add({
					id: newId,
					project_id: projectId,
					label: node.label,
					type: node.type === "react" ? "react" : "python",
					outputs: Array.isArray(node.outputs)
						? node.outputs.filter(
								(value): value is string => typeof value === "string",
							)
						: [],
					inputs: toRecord(node.inputs) as ProjectNodeInputs,
					position: {
						x: position.x + offset,
						y: position.y + offset,
					},
					code: node.code,
					metadata: sanitizeMetadata(node.metadata),
				}),
			).server
		}

		const pastedEdgeIds: string[] = []
		for (const edge of clipboard.edges) {
			const sourceNodeId = nodeIdMap.get(edge.source_node_id)
			const targetNodeId = nodeIdMap.get(edge.target_node_id)
			const sourceHandle = toHandle(edge.source_handle)
			const targetHandle = toHandle(edge.target_handle)
			if (!sourceNodeId || !targetNodeId || !sourceHandle || !targetHandle)
				continue
			const edgeId = uuidv7()
			pastedEdgeIds.push(edgeId)

			await z.mutate(
				mutators.edges.add({
					id: edgeId,
					project_id: projectId,
					source_node_id: sourceNodeId,
					source_handle: sourceHandle,
					target_node_id: targetNodeId,
					target_handle: targetHandle,
				}),
			).server
		}

		// ensure pasted elements are the active selection so user can move directly
		let retries = 0
		const maxRetries = 20
		const selectPastedElements = () => {
			const state = storeApi.getState()
			const nodesReady = pastedNodeIds.every((id) =>
				state.nodes.some((node) => node.id === id),
			)
			const edgesReady = pastedEdgeIds.every((id) =>
				state.edges.some((edge) => edge.id === id),
			)

			if ((!nodesReady || !edgesReady) && retries < maxRetries) {
				retries += 1
				setTimeout(selectPastedElements, 16)
				return
			}

			const activeElement = document.activeElement
			if (
				activeElement instanceof HTMLInputElement ||
				activeElement instanceof HTMLTextAreaElement ||
				(activeElement instanceof HTMLElement &&
					activeElement.isContentEditable)
			) {
				activeElement.blur()
			}

			applySelection(storeApi, pastedNodeIds, pastedEdgeIds)
		}

		selectPastedElements()
		return true
	}, [projectId, storeApi, z])

	return { copySelection, hasClipboardSelection, pasteSelection }
}
